/**
 * The **pure join** behind CODEX — the content-only lookups the dossier browser needs on top
 * of a plain `CodexContent`.
 *
 * ── ONE AUDIENCE, ONE PAYLOAD (`decisions/0157`, the reveal-plane retirement) ──
 *
 * `<world>/codex.json` **is** the gate: the server serves it whole to every recipient, and
 * issuing a new record to the table is an edit to that file (`codex/README.md`), not a runtime
 * command. So every function here takes only content — no reveal map, no audience — and every
 * entry the content holds is simply shown. `cli/src/codex-cli.ts` reads the same content.
 *
 * What's here:
 *
 *  - {@link affiliationChip} — the chip's label, `???` when none is authored.
 *  - {@link locationRow} — an entry's LAST KNOWN LOCATION, resolved to the place's file.
 *  - {@link crossLinks} / {@link namedIn} — the files this one links to, and the files that
 *    link to it (F5, `decisions/0205` point 5).
 *  - {@link briefFiles} / {@link kindTallies} — the reader's brief when nothing is open (F2).
 *  - the index (`.rlx-spine`) — grouping, search, class filter and keyboard cursor.
 *  - the RECORD column's stamp/annotation split and the identity column's ledger rows.
 */
import type { CodexEntry, CodexEntryId, CodexKind } from "@lancer-vtt/domain";

/**
 * The affiliation chip's label — the authored affiliation upper-cased, or `???` when the
 * entry carries none. `???` is a legible state in its own right, not a gap: an entry the
 * table has met and cannot place.
 */
export function affiliationChip(entry: CodexEntry): string {
  return (entry.affiliation ?? "???").toUpperCase();
}

/**
 * The card's LAST KNOWN LOCATION row, resolved against the codex: the place's file to open,
 * or `"none"` when the entry records no location or the reference is dangling.
 */
export type LocationRow = { kind: "none" } | { kind: "known"; entryId: CodexEntryId; name: string };

export function locationRow(entry: CodexEntry, index: Map<CodexEntryId, CodexEntry>): LocationRow {
  if (entry.location === undefined) return { kind: "none" };
  const place = index.get(entry.location);
  // A dangling location can't happen through the validator against the repo-versioned seed
  // (strict mode resolves every reference), but this module is handed content over the wire,
  // and the loader's own drop-and-warn softening means a live server may legitimately have
  // dropped this reference already — so this degrades rather than throws.
  if (place === undefined) return { kind: "none" };
  return { kind: "known", entryId: place.id, name: place.name };
}

/**
 * The cross-link chips a card shows: the entries this one links to, in authored link order,
 * self-links and dangling targets dropped.
 */
export function crossLinks(entry: CodexEntry, index: Map<CodexEntryId, CodexEntry>): CodexEntry[] {
  const out: CodexEntry[] = [];
  for (const id of entry.links) {
    if (id === entry.id) continue; // a self-link is legal content; a chip back to this card is noise
    const target = index.get(id);
    if (target === undefined) continue;
    out.push(target);
  }
  return out;
}

/**
 * Every entry's citing files: entry id → the entries whose `links` name it, in authored order,
 * each once, self-links skipped. "Cited" means linked; a `location` is not a citation.
 */
export function inboundLinks(entries: readonly CodexEntry[]): Map<CodexEntryId, CodexEntry[]> {
  const inbound = new Map<CodexEntryId, CodexEntry[]>(entries.map((e) => [e.id, []]));
  for (const from of entries) {
    for (const to of new Set(from.links)) {
      if (to === from.id) continue;
      inbound.get(to)?.push(from);
    }
  }
  return inbound;
}

/**
 * NAMED IN (F5): the files that link to `entry` and that it doesn't already link back to —
 * a mutual link is already an ASSOCIATED RECORDS chip, and listing it twice is noise.
 */
export function namedIn(entry: CodexEntry, inbound: Map<CodexEntryId, CodexEntry[]>): CodexEntry[] {
  const outbound = new Set(entry.links);
  return (inbound.get(entry.id) ?? []).filter((from) => !outbound.has(from.id));
}

/** One brief card: the entry and how many files cite it. */
export type BriefFile = { entry: CodexEntry; cited: number };

/**
 * The brief's START HERE row (F2): the `limit` most-cited files, worked out from the codex's
 * own links, most-cited first and authored order on a tie. A file nobody cites isn't a place
 * to start, so it never makes the row even when fewer than `limit` qualify.
 */
export function briefFiles(entries: readonly CodexEntry[], limit = 6): BriefFile[] {
  const inbound = inboundLinks(entries);
  return entries
    .map((entry) => ({ entry, cited: inbound.get(entry.id)?.length ?? 0 }))
    .filter((f) => f.cited > 0)
    .sort((a, b) => b.cited - a.cited)
    .slice(0, limit);
}

/** A brief card's excerpt: the body of the entry's first filed record (not an annotation), or `""`. */
export function firstRecordExcerpt(entry: CodexEntry): string {
  const record = entry.facts.find((f) => splitAnnotation(f.text) === null);
  return record === undefined ? "" : splitStamp(record.text).body;
}

// ── the index (`.rlx-spine`) — Phase 4 of `codex-v1`, plan.md §6 ──────────────────────
//
// The index lists every file, grouped by class. Every function below is pure content shaping
// — no DOM, no React — so the grouping, the fixed class order and the "no heading for an
// empty class" rule are unit-testable without a renderer. They take any {@link SpineItem}: a
// codex entry is one as it stands, and a lancer row is one built by `RolodexApp`.

/** An index class: a codex kind, or `lancer` — a player's pilot, filed or not (`decisions/0205`). */
export type IndexKind = "lancer" | CodexKind;

/** What the index needs of a row: its id, class, name and the line under the name. */
export type SpineItem = {
  id: string;
  kind: IndexKind;
  name: string;
  role?: string;
  /** A third line, searched like the other two — a lancer's `PLAYER · MECH`. */
  meta?: string;
};

/**
 * The index's fixed grouping order — **not** {@link CODEX_KINDS}' declaration order. LANCERS
 * lead (`decisions/0205` point 6); then person → place → org → faction → ship, so a `faction`
 * (a power) sorts AFTER `org` (a body).
 */
export const SPINE_KIND_ORDER: readonly IndexKind[] = ["lancer", "person", "place", "org", "faction", "ship"];

/**
 * The index's group heading for each class. `faction` (a **power**, see {@link CodexKind})
 * heads as POWERS and `ship` as VESSELS; the rest are the plural of the class itself.
 */
export const SPINE_KIND_HEADING: Record<IndexKind, string> = {
  lancer: "LANCERS",
  person: "PERSONS",
  place: "PLACES",
  org: "ORGS",
  faction: "POWERS",
  ship: "VESSELS",
};

/** The class-filter chips, in {@link SPINE_KIND_ORDER} — the class's SINGULAR noun, where the
 *  group heading above is its plural. Keyboard `1`–`6` toggle these in this order. */
export const SPINE_FILTER_CHIPS: readonly { kind: IndexKind; label: string }[] = [
  { kind: "lancer", label: "LANCER" },
  { kind: "person", label: "PERSON" },
  { kind: "place", label: "PLACE" },
  { kind: "org", label: "ORG" },
  { kind: "faction", label: "POWER" },
  { kind: "ship", label: "VESSEL" },
];

/** One grouped section of the index: a class, its heading, and the rows filed under it. */
export type SpineGroup<T extends SpineItem = SpineItem> = { kind: IndexKind; heading: string; entries: T[] };

/**
 * Groups `entries` into the index's fixed class order, each row's relative (authored) order
 * preserved within its group. **A class with nothing in it prints no heading at all** — the
 * group is simply absent from the returned list, not present-with-zero-rows.
 */
export function spineGroups<T extends SpineItem>(entries: readonly T[]): SpineGroup<T>[] {
  const out: SpineGroup<T>[] = [];
  for (const kind of SPINE_KIND_ORDER) {
    const matched = entries.filter((e) => e.kind === kind);
    if (matched.length === 0) continue;
    out.push({ kind, heading: SPINE_KIND_HEADING[kind], entries: matched });
  }
  return out;
}

/** `entries`, in the exact order the index renders them (grouped, then flattened) — the order
 *  the keyboard cursor steps through. */
export function spineFlatOrder<T extends SpineItem>(entries: readonly T[]): T[] {
  return spineGroups(entries).flatMap((g) => g.entries);
}

/**
 * Whether a row matches the search field's `query` — case-insensitive, matched against the
 * lines the row itself shows (name, the line under it, a lancer's meta line), so a match is
 * never a surprise. An empty/whitespace-only query matches everything.
 */
export function matchesSpineQuery(entry: SpineItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  return [entry.name, entry.role, entry.meta].some((line) => line !== undefined && line.toLowerCase().includes(q));
}

/**
 * The index's combined filter: a search query plus zero or more armed class chips. `kinds`
 * empty ⇒ every class passes — a chip NARROWS the file, it doesn't have to be armed to see
 * anything.
 */
export function filterSpineEntries<T extends SpineItem>(entries: readonly T[], opts: { query: string; kinds: ReadonlySet<IndexKind> }): T[] {
  return entries.filter((e) => (opts.kinds.size === 0 || opts.kinds.has(e.kind)) && matchesSpineQuery(e, opts.query));
}

/** One BY CLASS tally on the brief: a class and how many rows it holds, in the index's order. */
export function kindTallies(entries: readonly SpineItem[]): { kind: IndexKind; heading: string; count: number }[] {
  return spineGroups(entries).map((g) => ({ kind: g.kind, heading: g.heading, count: g.entries.length }));
}

/**
 * Steps the keyboard cursor by `delta` (`1` = ↓, `-1` = ↑) through `order` (the index's current
 * flat traversal order — post-filter, so the cursor only ever lands on a visible row).
 * Clamps at either end rather than wrapping. `currentId === null` (no cursor yet, or the
 * cursor's former row was filtered out) lands on the first row for ↓ and the last for ↑.
 */
export function spineCursorStep(order: readonly SpineItem[], currentId: string | null, delta: 1 | -1): string | null {
  if (order.length === 0) return null;
  const idx = currentId === null ? -1 : order.findIndex((e) => e.id === currentId);
  if (idx === -1) return (delta === 1 ? order.at(0) : order.at(-1))!.id;
  const next = idx + delta;
  if (next < 0 || next >= order.length) return order[idx]!.id;
  return order[next]!.id;
}

// ── the RECORD column (`.rlx-record`) — Phase 5 of `codex-v1`, plan.md §6 ─────────────
//
// The right column of the file view. A fact's `text` is one authored string with no structured
// stamp/body split in the schema — the two functions below recover that split by convention, so
// the reader never sees an unbroken wall of prose. Both are pure string functions, no DOM.

/**
 * Splits a filed record's `text` into its leading print-stamp and the prose body beneath it.
 * `"PERSONNEL FILE — GRADE: junior…"` → `{ stamp: "PERSONNEL FILE", body: "GRADE: junior…" }`.
 * The stamp is a leading ALL-CAPS run (letters, digits, space, and `,./&-` — so
 * `"MANIFEST, MISSION-CRITICAL"` and `"COLONIAL ESCORT TASKING, RELATION TO"` both qualify), at
 * most 40 characters, immediately followed by `:` or ` — ` (an em dash in spaces). Either
 * separator is consumed and never appears in `body`.
 *
 * Every one of the served seed's 45 non-annotation facts matches this — but the fallback is
 * not dead code: the developer hand-edits `codex/session-one.codex.json` directly and will
 * eventually write a line with no stamp. On no match, `stamp` is `null` and `body` is `text`
 * **unchanged** — the caller's job is then to render the raw string exactly as filed, with no
 * stamp line above it.
 */
export function splitStamp(text: string): { stamp: string | null; body: string } {
  const match = /^([A-Z][A-Z0-9 ,./&-]{0,39})(?::\s*|\s—\s)/.exec(text);
  if (match === null) return { stamp: null, body: text };
  return { stamp: match[1]!.trimEnd(), body: text.slice(match[0].length) };
}

/**
 * Splits an `ANNOTATIONS —` fact into its quote and byline. A fact whose `text` matches this
 * shape is speech, not a filed record — it leaves the numbered RECORD list entirely and renders
 * instead as the lifted-out `ANNOTATIONS` block (`RolodexApp.tsx`'s viewing branch), the quote
 * in Archivo (not mono) because it is a voice, not paperwork.
 *
 * `^ANNOTATIONS\s*—\s*([^:]+):\s*(.*)$` — the byline is everything up to the FIRST colon, so a
 * byline that itself carries a comma resolves correctly either way the comma falls:
 * `"ANNOTATIONS — T. BRANDT, FOREMAN: …"` → byline `"T. BRANDT, FOREMAN"`, and
 * `"ANNOTATIONS — FILED BY PEETY, ADMINISTRATIVE NHP: …"` → byline
 * `"FILED BY PEETY, ADMINISTRATIVE NHP"` — both real, both in the served seed. Returns `null`
 * on no match, the fallback the caller uses to leave a non-matching fact in the numbered
 * RECORD list unchanged.
 */
export function splitAnnotation(text: string): { byline: string; quote: string } | null {
  const match = /^ANNOTATIONS\s*—\s*([^:]+):\s*(.*)$/.exec(text);
  if (match === null) return null;
  return { byline: match[1]!.trim(), quote: match[2]! };
}

// ── the identity plate (`.rlx-lcol`) — Phase 5 of `codex-v1`, plan.md §6 ──────────────
//
// The file view's left column. Three content decisions live here, each with a defined empty
// state rather than a hidden row — `LAST KNOWN LOCATION`'s own three-way rendering already has
// one ({@link locationRow}'s `"none"` case, reused as-is by the caller); these two give
// `STANDING`/`ALSO KNOWN AS` the same `—` convention rather than inventing a second one.

/**
 * The ledger's `STANDING` row — the entry's own `role`, or `—` when none is authored. `role` is
 * genuinely optional in the schema (`CodexEntry.role?: string`) even though every entry in the
 * served seed today happens to carry one; the empty state exists for the schema's sake, not
 * because the corpus currently exercises it.
 */
export function standingRow(entry: CodexEntry): string {
  return entry.role ?? "—";
}

/**
 * The ledger's `ALSO KNOWN AS` row — the entry's aliases, `· `-joined, or `—` when none are
 * authored. `aliases` always normalizes to at least `[]` at load (never absent), so an empty
 * array — not an absent key — is the seed's own encoding of "no known aliases," and 13 of the
 * served corpus's 19 entries carry exactly that.
 */
export function aliasesRow(entry: CodexEntry): string {
  return entry.aliases.length > 0 ? entry.aliases.join(" · ") : "—";
}

/**
 * The identity plate's `FILE` reference — `UCA/R10 · <id>`. `CodexEntry` has no serial field,
 * so the entry's own id is the real file reference; an
 * invented numeric serial (`R10-0114`) was considered and rejected (plan.md §10) because
 * nothing in the schema backs it.
 */
export function fileReference(entry: CodexEntry): string {
  return `UCA/R10 · ${entry.id.toUpperCase()}`;
}
